import { LitElement, html, css } from 'lit';

/**
 * CrusherPaymentMethodCard
 *
 * Billing-page primitive, displays a customer's saved payment method (card
 * brand + last 4 + expiry) with a CTA slot for manage/update actions.
 *
 * Designed for products using a billing provider that hosts the actual card
 * collection / management UI (Polar, Stripe Billing customer portal, Paddle).
 * The product fetches saved-card metadata via the billing API and passes it
 * here; the consumer wires the CTA button (typically a link to the provider's
 * hosted portal) in the `cta` slot.
 *
 * Two states:
 *   - has-card: render brand + last4 + expiry + optional billing email
 *   - none: render "No payment method on file" + the cta slot
 *
 * Properties:
 *   - brand: card brand string ("visa" / "mastercard" / "amex" /
 *                    "discover" / "diners" / "jcb" / "unionpay" / "unknown")
 *                    Case-insensitive; matches Stripe / Polar / Paddle convention.
 *   - last4: last 4 digits string (e.g., "4242")
 *   - exp-month: expiry month (1-12)
 *   - exp-year: expiry year (4-digit)
 *   - email: optional billing email displayed under the card
 *   - default: boolean; show a "Default" badge
 *   - expired: boolean; render with warning tone + "Expired" badge
 *
 * Slots:
 *   - cta, primary action button (typical: "Manage payment methods" linking
 *           to the provider's hosted customer portal)
 *
 * A11y: role=region with aria-label combining brand + last4 so screen readers
 *       announce "Visa ending in 4242".
 *
 * Why no event handlers: card-management actions are inherently external
 * (provider-hosted portal). This is a display surface, not interactive.
 */
export class CrusherPaymentMethodCard extends LitElement {
  static properties = {
    brand:    { type: String, reflect: true },
    last4:    { type: String, reflect: true },
    expMonth: { type: Number, attribute: 'exp-month', reflect: true },
    expYear:  { type: Number, attribute: 'exp-year', reflect: true },
    email:    { type: String, reflect: true },
    default:  { type: Boolean, reflect: true },
    expired:  { type: Boolean, reflect: true }
  };

  constructor() {
    super();
    this.brand = '';
    this.last4 = '';
    this.expMonth = 0;
    this.expYear = 0;
    this.email = '';
    this.default = false;
    this.expired = false;
  }

  static styles = css`
    :host {
      display: block;
      background: var(--crusher-surface);
      color: var(--crusher-fg);
      border: var(--crusher-component-border-weight) solid var(--crusher-border);
      border-radius: var(--crusher-radius-lg);
      /* Generous internal padding so brand tile, label/meta, and action
         buttons all sit well clear of the card border. spacing-6 vertical
         (24px) and spacing-7 horizontal (28px) match typical Stripe /
         Polar billing row densities. */
      padding: var(--crusher-spacing-6) var(--crusher-spacing-7);
      position: relative;
    }
    /* Default state gets a subtle brand accent on the left edge so the
       "primary card" reads at a glance, even before the badge registers. */
    :host([default]) {
      border-color: color-mix(in srgb, var(--crusher-color-brand-primary) 30%, var(--crusher-border));
    }
    :host([default])::before {
      content: '';
      position: absolute;
      inset-block: 1.125rem;
      inset-inline-start: 0;
      inline-size: 3px;
      border-radius: 0 var(--crusher-radius-full) var(--crusher-radius-full) 0;
      background: var(--crusher-color-brand-primary);
    }
    :host([expired]) {
      border-color: color-mix(in srgb, var(--crusher-feedback-danger) 45%, var(--crusher-border));
      background: color-mix(in srgb, var(--crusher-feedback-danger) 4%, var(--crusher-surface));
    }
    :host([expired])::before {
      content: '';
      position: absolute;
      inset-block: 1.125rem;
      inset-inline-start: 0;
      inline-size: 3px;
      border-radius: 0 var(--crusher-radius-full) var(--crusher-radius-full) 0;
      background: var(--crusher-feedback-danger);
    }

    .row {
      display: flex;
      align-items: center;
      gap: var(--crusher-spacing-4);
      flex-wrap: wrap;
    }

    /* Brand-mark tile: real ISO 7810 ID-1 aspect (1.586) at a larger size
       so it actually reads as a credit card, not a generic colored tag.
       Includes a chip (small rounded rectangle, top-left), a brand label
       centered, and a 4-dot "card number" hint along the bottom edge so
       the visual carries the meaning of "this represents a saved card"
       at a glance. Brand-color backgrounds match each network's known palette
       (text labels, no copyrighted brand SVGs). */
    .brand-icon {
      flex: none;
      inline-size: 4rem;
      block-size: 2.5rem;
      border-radius: var(--crusher-radius-sm);
      background: color-mix(in srgb, var(--crusher-fg) 10%, transparent);
      color: var(--crusher-fg);
      display: grid;
      place-items: center;
      font-weight: var(--crusher-font-weight-bold);
      font-size: 0.8125rem;
      letter-spacing: 0.04em;
      position: relative;
      overflow: hidden;
      box-shadow:
        inset 0 1px 0 color-mix(in srgb, white 16%, transparent),
        inset 0 -2px 4px color-mix(in srgb, black 20%, transparent);
    }
    /* Chip rectangle (top-left) */
    .brand-icon::before {
      content: '';
      position: absolute;
      top: 0.375rem;
      left: 0.375rem;
      width: 0.5rem;
      height: 0.375rem;
      border-radius: 1px;
      background: color-mix(in srgb, currentColor 45%, transparent);
      box-shadow: inset 0 0 0 0.5px color-mix(in srgb, currentColor 65%, transparent);
    }
    /* 4-dot card-number hint (bottom edge) */
    .brand-icon::after {
      content: '';
      position: absolute;
      bottom: 0.375rem;
      right: 0.5rem;
      inline-size: 0.875rem;
      block-size: 0.1875rem;
      background-image: radial-gradient(circle, currentColor 32%, transparent 36%);
      background-size: 0.25rem 0.1875rem;
      background-repeat: repeat-x;
      opacity: 0.55;
    }
    .brand-icon[data-brand="visa"]       { background: #1a1f71; color: #ffffff; }
    .brand-icon[data-brand="mastercard"] { background: #1a1a1a; color: #ff9b3d; }
    .brand-icon[data-brand="amex"]       { background: #016fd0; color: #ffffff; }
    .brand-icon[data-brand="discover"]   { background: #ff6f00; color: #ffffff; }
    .brand-icon[data-brand="diners"]     { background: #0079be; color: #ffffff; }
    .brand-icon[data-brand="jcb"]        { background: #0e4c96; color: #ffffff; }
    .brand-icon[data-brand="unionpay"]   { background: #d10429; color: #ffffff; }

    .details {
      flex: 1 1 12rem;
      min-inline-size: 0;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }
    .label {
      font-weight: var(--crusher-font-weight-semibold);
      font-size: var(--crusher-font-size-base);
      line-height: 1.3;
      color: var(--crusher-fg);
      margin: 0;
    }
    .meta {
      color: var(--crusher-fg-muted);
      font-size: var(--crusher-font-size-sm);
      line-height: 1.4;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.375rem;
    }
    .meta-dot {
      inline-size: 0.1875rem;
      block-size: 0.1875rem;
      border-radius: 50%;
      background: currentColor;
      opacity: 0.5;
    }

    /* Actions column: badge stacked above the CTA button, both right-aligned.
       Predictable position regardless of card state (no badge: just CTA;
       with badge: badge + gap + CTA). */
    .actions {
      flex: none;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: var(--crusher-spacing-2);
    }
    .badges {
      display: inline-flex;
      gap: var(--crusher-spacing-2);
      block-size: 1.25rem; /* reserve so cards align with and without badges */
      align-items: center;
    }
    .badge {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.1875rem 0.5rem;
      border-radius: var(--crusher-radius-full);
      font-size: 0.6875rem;
      font-weight: var(--crusher-font-weight-semibold);
      line-height: 1;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      border: 1px solid transparent;
      background: transparent;
    }
    .badge-default {
      color: var(--crusher-color-brand-primary);
      border-color: color-mix(in srgb, var(--crusher-color-brand-primary) 40%, transparent);
      background: color-mix(in srgb, var(--crusher-color-brand-primary) 8%, transparent);
    }
    .badge-expired {
      color: var(--crusher-feedback-danger);
      border-color: color-mix(in srgb, var(--crusher-feedback-danger) 40%, transparent);
      background: color-mix(in srgb, var(--crusher-feedback-danger) 8%, transparent);
    }
    .cta ::slotted(*) { font: inherit; }

    /* Empty state: visually distinct surface, not a degraded "saved card"
       row. Dashed border around the host, centered prompt with leading
       plus icon, CTA pinned right. */
    :host(.is-empty) {
      border-style: dashed;
      background: color-mix(in srgb, var(--crusher-fg) 2%, transparent);
    }
    .empty-row {
      display: flex;
      align-items: center;
      gap: var(--crusher-spacing-4);
      flex-wrap: wrap;
    }
    .empty-icon {
      flex: none;
      inline-size: 2.25rem;
      block-size: 2.25rem;
      border-radius: var(--crusher-radius-full);
      background: color-mix(in srgb, var(--crusher-fg) 8%, transparent);
      color: var(--crusher-fg-muted);
      display: grid;
      place-items: center;
    }
    .empty-text {
      flex: 1 1 8rem;
      min-inline-size: 0;
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }
    .empty-title {
      color: var(--crusher-fg);
      font-weight: var(--crusher-font-weight-medium);
      font-size: var(--crusher-font-size-sm);
      line-height: 1.3;
    }
    .empty-meta {
      color: var(--crusher-fg-muted);
      font-size: var(--crusher-font-size-xs);
      line-height: 1.3;
    }

    @media (max-width: 480px) {
      :host { padding: var(--crusher-spacing-5) var(--crusher-spacing-5); }
      .row, .empty-row { gap: var(--crusher-spacing-3); }
      .actions { width: 100%; align-items: stretch; }
      .badges { justify-content: flex-end; }
      .cta { width: 100%; }
      .cta ::slotted(*) { width: 100%; }
    }
  `;

  _brandLabel(brand) {
    const b = (brand || '').toLowerCase();
    switch (b) {
      case 'visa':       return 'VISA';
      case 'mastercard': return 'MC';
      case 'amex':
      case 'american_express':
      case 'american express':
        return 'AMEX';
      case 'discover':   return 'DISC';
      case 'diners':
      case 'diners_club':
        return 'DINERS';
      case 'jcb':        return 'JCB';
      case 'unionpay':   return 'UP';
      default:           return 'CARD';
    }
  }

  _brandDisplay(brand) {
    const b = (brand || '').toLowerCase();
    switch (b) {
      case 'visa':       return 'Visa';
      case 'mastercard': return 'Mastercard';
      case 'amex':
      case 'american_express':
      case 'american express':
        return 'American Express';
      case 'discover':   return 'Discover';
      case 'diners':
      case 'diners_club':
        return 'Diners Club';
      case 'jcb':        return 'JCB';
      case 'unionpay':   return 'UnionPay';
      default:           return 'Card';
    }
  }

  _expiryDisplay() {
    if (!this.expMonth || !this.expYear) return '';
    const mm = String(this.expMonth).padStart(2, '0');
    /* Display 2-digit year (1234 → 34) to match standard card-display convention */
    const yy = String(this.expYear).slice(-2);
    return `Expires ${mm}/${yy}`;
  }

  render() {
    const hasCard = Boolean(this.last4);
    const brandKey = (this.brand || '').toLowerCase();
    const ariaLabel = hasCard
      ? `${this._brandDisplay(this.brand)} ending in ${this.last4}`
      : 'No payment method on file';

    /* Tag the host with is-empty so the CSS can switch to the dashed empty
       surface treatment without the consumer thinking about variants. */
    this.classList.toggle('is-empty', !hasCard);

    if (!hasCard) {
      return html`
        <div class="empty-row" role="region" aria-label=${ariaLabel}>
          <span class="empty-icon" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <line x1="8" y1="3" x2="8" y2="13"/>
              <line x1="3" y1="8" x2="13" y2="8"/>
            </svg>
          </span>
          <div class="empty-text">
            <span class="empty empty-title">No payment method on file.</span>
            <span class="empty-meta">Add a card to enable billing.</span>
          </div>
          <div class="actions">
            <div class="cta"><slot name="cta"></slot></div>
          </div>
        </div>
      `;
    }

    const expiry = this._expiryDisplay();
    const hasBadge = this.default || this.expired;
    const hasMeta = Boolean(expiry || this.email);

    return html`
      <div class="row" role="region" aria-label=${ariaLabel}>
        <div class="brand-icon" data-brand=${brandKey} aria-hidden="true">${this._brandLabel(this.brand)}</div>
        <div class="details">
          <span class="label">${this._brandDisplay(this.brand)} ending in ${this.last4}</span>
          ${hasMeta ? html`
            <span class="meta">
              ${expiry ? html`<span>${expiry}</span>` : null}
              ${expiry && this.email ? html`<span class="meta-dot" aria-hidden="true"></span>` : null}
              ${this.email ? html`<span>${this.email}</span>` : null}
            </span>
          ` : null}
        </div>
        <div class="actions">
          ${hasBadge ? html`
            <span class="badges">
              ${this.default ? html`<span class="badge badge-default">Default</span>` : null}
              ${this.expired ? html`<span class="badge badge-expired">Expired</span>` : null}
            </span>
          ` : html`<span class="badges" aria-hidden="true"></span>`}
          <div class="cta"><slot name="cta"></slot></div>
        </div>
      </div>
    `;
  }
}

customElements.define('crusher-payment-method-card', CrusherPaymentMethodCard);
